import { html, LitElement, type PropertyValues } from 'lit'
import { customElement, property, queryAssignedElements, state } from 'lit/decorators.js'
import { $t, localized } from '@/navbar/i18n'
import type { MenuItem } from '@/navbar/navbar.types'
import { DEFAULT_HELP_URL } from '@/navbar/constants'
import { ifDefined } from 'lit/directives/if-defined.js'

// Child components:
import '../menu/eco-suite-navbar-menu.component'
import '../button/eco-suite-navbar-button.component'
import '../menu-item/eco-suite-navbar-menu-item.component'

/**
 * @internal
 */
@localized()
@customElement('eco-suite-navbar-help')
export class EcoSuiteNavbarHelp extends LitElement {
  @queryAssignedElements()
  private readonly slottedElements!: Array<HTMLSlotElement>

  @property({ attribute: false })
  menuItems?: MenuItem[]

  @property()
  href?: string

  @property({ attribute: false })
  onHelpClick?: (e: Event) => void

  @state()
  private hasMenu = false

  get _href () {
    if (this.href) return this.href
    if (this.onHelpClick) return undefined
    return DEFAULT_HELP_URL
  }

  protected willUpdate(changedProps: PropertyValues) {
    if (changedProps.has('menuItems')) {
      this.hasMenu = !!(this.menuItems?.length)
    }
  }

  private async checkIfMenuShouldDisplay() {
    if (this.menuItems?.length) {
      this.hasMenu = true
      return
    }

    const slottedElement = this.slottedElements[0]
    if (!slottedElement) {
      this.hasMenu = false
      return
    }

    const isSlot = (e: Element) => e?.tagName.toLowerCase() === 'slot'

    if (isSlot(slottedElement)) {
      /*
        On desktop, the help-menu slot passes through two components (eco-suite-navbar > eco-suite-navbar-help)
        On mobile, it passes through three components (eco-suite-navbar > eco-suite-navbar-mobile-menu > eco-suite-navbar-help)
        We need to resolve the FINAL assigned elements to determine if there are any menu items present
      */
      const firstAssignedElement = slottedElement.assignedElements?.()[0]

      if (isSlot(firstAssignedElement)) {
        const secondAssignedElement = (firstAssignedElement as HTMLSlotElement).assignedElements?.()[0]
        this.hasMenu = !!(secondAssignedElement)
      } else {
        this.hasMenu = !!firstAssignedElement
      }
    } else {
      this.hasMenu = true
    }
  }

  render() {
    // If there is a Help item click handler, don't assume it's an external link
    // as it may just open a Help dialog
    const externalHelpLink = !this.onHelpClick

    if (this.hasMenu) {
      return html`
        <eco-suite-navbar-menu
          label=${$t('navbar.help')}
          icon="help"
          data-qa-id="help-menu"
          hide-menu-header
        >
          ${this.menuItems?.map((item) => html`
            <eco-suite-navbar-menu-item
              .icon=${item.icon}
              .label=${item.label}
              .href=${item.href}
              .onClick=${item.onClick}
              .isCurrent=${item.isCurrent}
              .target=${item.target}
              .isExternal=${item.isExternal}
              data-qa-id=${ifDefined(item.dataQaId)}
            ></eco-suite-navbar-menu-item>
          `)}
          <slot @slotchange=${this.checkIfMenuShouldDisplay}></slot>
          <eco-suite-navbar-menu-item
            icon="help"
            label=${$t('navbar.help')}
            .href=${this._href}
            .target=${externalHelpLink ? '_blank' : undefined}
            .isExternal=${externalHelpLink}
            .onClick=${this.onHelpClick}
            data-qa-id="help-menu-button"
          ></eco-suite-navbar-menu-item>
        </eco-suite-navbar-menu>
      `
    }

    return html`
      <eco-suite-navbar-button
        icon="help"
        label=${$t('navbar.help')}
        .href=${this._href}
        .target=${externalHelpLink ? '_blank' : undefined}
        .isExternal=${externalHelpLink}
        .onClick=${this.onHelpClick}
        data-qa-id="help-menu-button"
      ></eco-suite-navbar-button>
      <slot
        style="display: none"
        @slotchange=${this.checkIfMenuShouldDisplay}
      ></slot>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'eco-suite-navbar-help': EcoSuiteNavbarHelp
  }
}
